import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import styled from "styled-components";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { useToasts } from "react-toast-notifications";
import { SG_UPDATE_EMPLOYEE_AUDIT, PULL_EMPLOYEE, SG_GET_EMPLOYEE_PERMISSIONS, SG_EDIT_EMPLOYEE_PERMISSION, SG_ADD_EMPLOYEE_PERMISSION } from "constants/actions";
import SignupToken from "components/SignupToken";
import EmailDeliveryReport from "components/EmailDeliveryReport";
import {
  ANALYTICS_CARD_PERMISSIONS,
  analyticsPermissionsToDetails,
  createAnalyticsPermissionState,
  extractEmployeeAuthDetails,
  hasAnyAnalyticsPermission,
  isAnalyticsAuthDetail,
  parseAnalyticsPermissionsFromDetails,
} from "../../utilities/analyticsPermissions";

// No detailed permissions here; only core employee flags
const PERMISSION_GROUPS = [
  {
    name: "Role",
    isRadio: true,
    items: [
      {
        key: "hasLogin",
        label: "User",
        description: "Allow this contact to sign in to the platform",
      },
      {
        key: "isAdminOption",
        label: "Admin",
        description: "Administrators have full access and settings control",
      },
    ],
  },
  {
    name: "Data & Insights",
    items: [
      {
        key: "analyticsPermissions",
        label: "Access Analytics",
        description: "Granular access to analytics dashboards",
        type: "analyticsAccordion",
      },
      // {
      //   key: "pdfReports",
      //   label: "Access PDF Reports",
      //   description: "Generate and download PDF reports",
      // },
      {
        key: "noraAI",
        label: "Nora AI Access",
        description: "Access to Nora AI features",
      },
      {
        key: "readFeedback",
        label: "Verbatim Comment Access",
        description: "Grants visibility into all verbatim comments from contacts and other open-text survey responses across the application.",
      },
      {
        key: "specialPermission",
        label: "Special Permission",
        description: "Has permission to view special anchors",
      },
    ],
  },
  {
    name: "Survey Management",
    items: [
      {
        key: "scheduleSurveys",
        label: "Schedule Surveys",
        description:
          "Ability to schedule and manage surveys for granted permission groups",
      },
      {
        key: "editUsers",
        label: "Manage Contacts",
        description:
          "Invite, edit, and remove contacts for granted permission groups",
      },
    ],
  }
];

// Toggle Component (reused from EditAudit.js)
const ToggleComponent = ({ checked, handleCheck, disabled = false, title }) => {
  const handleChange = () => {
    if (!disabled) {
      handleCheck(!checked);
    }
  };

  return (
    <ToggleLabel disabled={disabled} title={title}>
      <ToggleInput
        type="checkbox"
        onChange={handleChange}
        value={checked}
        checked={checked}
        disabled={disabled}
      />
      <ToggleSlider isChecked={checked} disabled={disabled} />
    </ToggleLabel>
  );
};

// Portal-based Tooltip Wrapper Component
const TooltipWrapperWithPortal = ({ children, tooltipText }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const wrapperRef = useRef(null);

  const updatePosition = () => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top - 8,
        left: rect.left + rect.width / 2,
      });
    }
  };

  const handleMouseEnter = () => {
    requestAnimationFrame(() => {
      updatePosition();
      setIsHovered(true);
    });
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  useEffect(() => {
    if (isHovered) {
      updatePosition();
    }
  }, [isHovered]);

  return (
    <>
      <TooltipWrapperPortal
        ref={wrapperRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </TooltipWrapperPortal>
      {isHovered &&
        ReactDOM.createPortal(
          <TooltipTextPortal style={{ top: `${position.top}px`, left: `${position.left}px` }}>
            {tooltipText}
          </TooltipTextPortal>,
          document.body
        )}
    </>
  );
};

const ManagerPermissions = ({ manager, group, isVisible, onGoToAssignContacts }) => {
  const dispatch = useDispatch();
  const { addToast } = useToasts();
  
  // Permission states - matching EditAudit.js structure
  const [analyticsPermissions, setAnalyticsPermissions] = useState(
    createAnalyticsPermissionState(false)
  );
  const [accessSubfactor, setAccessSubfactor] = useState(false);
  const [readFeedback, setReadFeedback] = useState(false);
  const [scheduleSurveys, setScheduleSurveys] = useState(false);
  const [editUsers, setEditUsers] = useState(false);
  const [hasLogin, setHasLogin] = useState(false);
  const [isAdminOption, setIsAdmin] = useState(false);
  const [pdfReports, setPdfReports] = useState(false);
  const [noraAI, setNoraAI] = useState(false);
  const [specialPermission, setSpecialPermission] = useState(false);
  const [isAnalyticsAccordionOpen, setAnalyticsAccordionOpen] = useState(true);

  const handleAnalyticsPermissionChange = (key, nextValue) => {
    setAnalyticsPermissions((prev) => ({
      ...prev,
      [key]: nextValue,
    }));
  };

  // Internal state
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Derived counts
  const assignedCount = Array.isArray(group?.members) ? group.members.length : 0;
  const hasAnalyticsAccess = hasAnyAnalyticsPermission(analyticsPermissions);
  const showAssignedWarning = hasAnalyticsAccess && !isAdminOption && assignedCount === 0;
  // Prevent backend detail mapping from overwriting UI choices during role switches
  const suppressDetailSyncRef = useRef(false);
  // Track the last initialized manager ID to prevent re-initialization when group prop changes
  const lastInitializedManagerIdRef = useRef(null);

  // Redux state
  const { get_auth, employeeById, employeePermission } = useSelector(
    (state) => ({
      get_auth: state.auth,
      employeeById: manager?.id ? state.employees?.[manager.id] : null,
      employeePermission: state.employee_permission,
    }),
    shallowEqual
  );

  // Ensure we have a fully hydrated employee object for mapping toggles
  useEffect(() => {
    if (manager?.id && isVisible) {
      dispatch({ type: PULL_EMPLOYEE, payload: { id: manager.id } });
      // Load detailed permissions (pdf, etc.)
      dispatch({ type: SG_GET_EMPLOYEE_PERMISSIONS, payload: `employee=${manager.id}` });
    }
  }, [dispatch, manager?.id, isVisible]);

  // Map actual manager core permissions to component state
  useEffect(() => {
    const src = employeeById || manager;
    const managerId = src?.id || manager?.id;

    // Only initialize if manager ID has changed or hasn't been initialized yet
    if (src && managerId && managerId !== lastInitializedManagerIdRef.current) {
      const initialReadFeedback = src.read_feedback === 1;
      const initialAnalytics = createAnalyticsPermissionState(src.access_data_analytics === 1);
      // Ensure feedback card respects readFeedback toggle from the start
      if (!initialReadFeedback) {
        initialAnalytics.feedback = false;
      }
      setAnalyticsPermissions(initialAnalytics);
      setAccessSubfactor(src.access_subfactor_data === 1);
      setEditUsers(src.survey_add_users === 1);
      setIsAdmin(src.account_type === 5);
      // Initialize hasLogin from source (do not force enable)
      setHasLogin(src.enable_login === true || src.enable_login === 1 || src.enable_login === '1');
      setReadFeedback(initialReadFeedback);
      setScheduleSurveys(src.survey_schedule === 1);

      // If admin, lock and elevate all permissions to true in UI
      if (src.account_type === 5) {
        setAnalyticsPermissions(createAnalyticsPermissionState(true));
        setAccessSubfactor(true);
        setEditUsers(true);
        setHasLogin(true);
        setReadFeedback(true);
        setScheduleSurveys(true);
        setPdfReports(true);
        setNoraAI(true);
        setSpecialPermission(true);
      }
      
      // Mark this manager ID as initialized
      lastInitializedManagerIdRef.current = managerId;
      setLoading(false);
    }
  }, [employeeById, manager?.id]);

  // Map detailed permissions to local state (PDF)
  useEffect(() => {
    if (!manager?.id) return;

    // Only process detailed permissions if this manager has been initialized for core permissions
    // This prevents re-initialization when group prop changes but manager ID hasn't changed
    if (lastInitializedManagerIdRef.current !== manager.id) {
      return;
    }

    const src = employeeById || manager;
    const isAdmin = isAdminOption === true || src?.account_type === 5;
    if (isAdmin) {
      setPdfReports(true);
      setNoraAI(true);
      setSpecialPermission(true);
      setAnalyticsPermissions(createAnalyticsPermissionState(true));
      return;
    }

    if (suppressDetailSyncRef.current) {
      return;
    }

    const authDetails = extractEmployeeAuthDetails(
      employeePermission?.employee_permission,
      manager.id
    );

    const hasPdf = (authDetails || []).some(
      (d) => d?.name === 'pdf_enabled' || d?.name_id === 'pdf' || d?.value === 'PDF - Enabled'
    );
    setPdfReports(Boolean(hasPdf));

    // Check for nora_ai - can be explicitly set to 'false' or 'true'
    const noraDetail = (authDetails || []).find(
      (d) => d?.value === 'nora_ai' || d?.name === 'nora_ai' || d?.name_id === 'nora_ai'
    );
    // If found, check if it's explicitly false; otherwise default to true for backward compatibility
    if (noraDetail) {
      // Check response field or if value is explicitly 'false'
      setNoraAI(noraDetail?.response !== false && noraDetail?.value !== 'false');
    } else {
      // Backward compatibility: if nora_ai doesn't exist, default to enabled
      setNoraAI(true);
    }

    // Check for special_permission
    const specialDetail = (authDetails || []).find(
      (d) => d?.value === 'special_permission' || d?.name === 'special_permission' || d?.name_id === 'special_permission'
    );
    setSpecialPermission(Boolean(specialDetail));

    const parsedAnalytics = parseAnalyticsPermissionsFromDetails(authDetails);
    if (parsedAnalytics) {
      // Ensure feedback card respects readFeedback toggle
      const finalAnalytics = { ...parsedAnalytics };
      if (!readFeedback) {
        finalAnalytics.feedback = false;
      }
      setAnalyticsPermissions(finalAnalytics);
    } else {
      const defaultAnalytics = createAnalyticsPermissionState(src?.access_data_analytics === 1);
      // Ensure feedback card respects readFeedback toggle
      if (!readFeedback) {
        defaultAnalytics.feedback = false;
      }
      setAnalyticsPermissions(defaultAnalytics);
    }
  }, [employeePermission, manager?.id, isAdminOption, employeeById]);

  // Reset suppression and initialization tracking when switching managers
  useEffect(() => {
    const managerId = manager?.id;
    const currentInitializedId = lastInitializedManagerIdRef.current;

    // If manager ID changed (or cleared), reset tracking to allow re-initialization
    if (managerId !== currentInitializedId) {
      suppressDetailSyncRef.current = false;
      // Only reset if we had a previous manager (not on first load)
      if (currentInitializedId !== null) {
        lastInitializedManagerIdRef.current = null; // Allow re-initialization for new manager
      }
    }
  }, [manager?.id]);

  // Sync feedback analytics card with readFeedback toggle
  // If readFeedback is turned off, feedback card must also be turned off
  useEffect(() => {
    if (!readFeedback && analyticsPermissions?.feedback === true) {
      setAnalyticsPermissions((prev) => ({
        ...prev,
        feedback: false,
      }));
    }
  }, [readFeedback, analyticsPermissions?.feedback]);

  // No detailed permission handling in this component

  // Handle saving permissions
  const handleSavePermissions = async () => {
    if (!manager?.id) {
      addToast("No manager selected", { appearance: "error" });
      return;
    }

    setIsSaving(true);

    try {
      // 1) Persist core audit fields on the employee (matches EditAudit.js)
      dispatch({
        type: SG_UPDATE_EMPLOYEE_AUDIT,
        payload: {
          // Persist login access from toggle
          enable_login: hasLogin === true,
          survey_add_users: editUsers ? 1 : 0,
          account_type: isAdminOption ? 5 : 9,
          survey_schedule: scheduleSurveys ? 1 : 0,
          // Always keep participation enabled (hidden from UI)
          survey_status: 1,
          read_feedback: readFeedback ? 1 : 0,
           // Always keep access_subfactor_data enabled (hidden from UI)
           access_subfactor_data: 1,
          access_data_analytics: hasAnalyticsAccess ? 1 : 0,
          // include names to mirror EditAudit.js behavior
          first_name: manager.first_name,
          last_name: manager.last_name,
          id: manager.id,
        },
      });

      // Persist detailed permissions: PDF and Nora AI (placeholder)
      const existing = employeePermission?.employee_permission?.find(
        (p) => p?.employee_id === Number(manager?.id)
      );
      const existingDetails = (existing?.auth_details || existing?.details || []).filter(
        (d) =>
          d?.name !== 'pdf_enabled' &&
          d?.name_id !== 'pdf' &&
          d?.value !== 'PDF - Enabled' &&
          d?.value !== 'nora_ai' &&
          d?.value_id !== 'nora_ai' &&
          d?.name !== 'nora_ai' &&
          d?.value !== 'special_permission' &&
          d?.value_id !== 'special_permission' &&
          d?.name !== 'special_permission' &&
          d?.name_id !== 'special_permission' &&
          !isAnalyticsAuthDetail(d)
      );
      const updatedDetails = [...existingDetails];
      if (pdfReports) {
        updatedDetails.push({
          name_id: 'pdf',
          name: 'pdf_enabled',
          value_id: 'pdf',
          value: 'PDF - Enabled',
          text: 'Enabled PDF',
        });
      }
      // Always include nora_ai - set value to 'true' or 'false' explicitly
      updatedDetails.push({
        value: noraAI ? 'nora_ai' : 'false',
        value_id: 'nora_ai',
        name: 'nora_ai',
        name_id: 'nora_ai',
        response: noraAI,
        text: noraAI ? 'Nora AI Access' : 'Nora AI Access (Disabled)'
      });

      // Include special_permission if enabled
      if (specialPermission) {
        updatedDetails.push({
          value: 'special_permission',
          value_id: 'special_permission',
          name: 'special_permission',
          name_id: 'special_permission',
          text: 'Special Permission'
        });
      }

      const analyticsDetails = analyticsPermissionsToDetails(analyticsPermissions);
      analyticsDetails.forEach((detail) => {
        updatedDetails.push(detail);
      });

      if (existing?.id) {
        dispatch({
          type: SG_EDIT_EMPLOYEE_PERMISSION,
          payload: {
            body: { id: existing.id, auth_details: updatedDetails },
            query: `organization=${get_auth?.organization_id}`,
          },
        });
      } else if (updatedDetails.length > 0) {
        dispatch({
          type: SG_ADD_EMPLOYEE_PERMISSION,
          payload: {
            body: { employee: Number(manager?.id), auth_details: updatedDetails },
            query: `organization=${get_auth?.organization_id}`,
          },
        });
      }

      addToast("Updated permissions", { appearance: "success" });
      // Allow backend details to map again after save
      suppressDetailSyncRef.current = false;
    } catch (error) {
      console.error("Error saving permissions:", error);
      addToast("Failed to update permissions", { appearance: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  if (!manager) {
    return (
      <Container>
        <EmptyState>
          <EmptyTitle>No Manager Selected</EmptyTitle>
          <EmptyText>Please select a group with an assigned manager to manage permissions.</EmptyText>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <HeaderSection>
        {showAssignedWarning && (
          <WarningBanner role="alert" aria-live="polite">
            <span>
              The following user does not have any assigned contacts, they won't be able to see any survey data
            </span>
            <AssignLinkButton
              type="button"
              onClick={() => (typeof onGoToAssignContacts === 'function' ? onGoToAssignContacts() : null)}
            >
              Click here to assign contacts
            </AssignLinkButton>
          </WarningBanner>
        )}
        <SaveButton onClick={handleSavePermissions} disabled={isSaving}>
          {isSaving ? "Saving..." : "Save Permissions"}
        </SaveButton>
      </HeaderSection>

      <PermissionsContent>
        <PermissionsTable>
          <TableHeader>
            <HeaderRow>
              <HeaderCell>Permission</HeaderCell>
              <HeaderCell>Description</HeaderCell>
              <HeaderCell style={{ width: '100px', textAlign: 'center' }}>Enabled</HeaderCell>
            </HeaderRow>
          </TableHeader>
          <TableBody>
            {PERMISSION_GROUPS.map((grouping) => (
              <React.Fragment key={grouping.name}>
                <GroupHeaderRow>
                  <GroupHeaderCell colSpan={3}>
                    <GroupTitle>{grouping.name}</GroupTitle>
                  </GroupHeaderCell>
                </GroupHeaderRow>

                {/* If group isRadio, render as a single radio group across its items */}
                {grouping.isRadio
                  ? (() => {
                      // Determine selected key for this radio group
                      const selectedKey = isAdminOption
                        ? 'isAdminOption'
                        : hasLogin
                        ? 'hasLogin'
                        : null;

                      const handleRadioSelect = (key) => {
                        if (key === 'isAdminOption') {
                          suppressDetailSyncRef.current = true;
                          // Selecting Admin mirrors prior toggle behavior
                          setIsAdmin(true);
                          setHasLogin(true);
                          setReadFeedback(true);
                          setScheduleSurveys(true);
                          setEditUsers(true);
                          setAnalyticsPermissions(createAnalyticsPermissionState(true));
                          setAccessSubfactor(true);
                          // Ensure admin has Nora + PDF + Special Permission enabled
                          setPdfReports(true);
                          setNoraAI(true);
                          setSpecialPermission(true);
                        } else if (key === 'hasLogin') {
                          suppressDetailSyncRef.current = true;
                          // Demote from admin; enable Access Analytics; deselect others
                          setIsAdmin(false);
                          setHasLogin(true);
                          setAnalyticsPermissions(createAnalyticsPermissionState(true));
                          setReadFeedback(false);
                          setScheduleSurveys(false);
                          setEditUsers(false);
                          setPdfReports(false);
                          setNoraAI(false);
                        }
                      };

                      const handleRadioToggle = (key) => {
                        // Clicking the already-selected radio will clear selection (restore none)
                        if (selectedKey === key) {
                          setIsAdmin(false);
                          setHasLogin(false);
                          setAnalyticsPermissions(createAnalyticsPermissionState(false));
                          return;
                        }
                        handleRadioSelect(key);
                      };

                      return grouping.items.map((permission) => (
                        <TableRow key={permission.key}>
                          <TableCell>
                            <RowLabelContainer>
                              <PermissionLabel>{permission.label}</PermissionLabel>
                            </RowLabelContainer>
                          </TableCell>
                          <TableCell>
                            <PermissionDescription>
                              {permission.description}
                            </PermissionDescription>
                          </TableCell>
                          <TableCell style={{ textAlign: 'center' }}>
                            <RadioWrapper>
                              <RadioInput
                                name={`radio-${grouping.name}`}
                                value={permission.key}
                                checked={selectedKey === permission.key}
                                onChange={() => handleRadioSelect(permission.key)}
                                onClick={() => handleRadioToggle(permission.key)}
                              />
                            </RadioWrapper>
                          </TableCell>
                        </TableRow>
                      ));
                    })()
                  : grouping.items.map((permission) => {
                      if (permission.type === 'analyticsAccordion') {
                        const accordionId = `analytics-accordion-${manager?.id || 'selection'}`;
                        return (
                          <React.Fragment key={permission.key}>
                            <TableRow>
                              <TableCell>
                                <RowLabelContainer>
                                  <PermissionLabel>{permission.label}</PermissionLabel>
                                </RowLabelContainer>
                              </TableCell>
                              <TableCell>
                                <PermissionDescription>
                                  {permission.description}
                                </PermissionDescription>
                              </TableCell>
                              <TableCell style={{ textAlign: 'right' }}>
                                <AccordionCaretButton
                                  type="button"
                                  onClick={() => setAnalyticsAccordionOpen((prev) => !prev)}
                                  aria-expanded={isAnalyticsAccordionOpen}
                                  aria-controls={accordionId}
                                  aria-label={isAnalyticsAccordionOpen ? 'Collapse analytics dashboards' : 'Expand analytics dashboards'}
                                >
                                  <AccordionCaretIcon $open={isAnalyticsAccordionOpen}>
                                    <svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                      <path
                                        d="M5.23 7.21a.75.75 0 011.06.02L10 10.585l3.71-3.354a.75.75 0 111.02 1.1l-4.25 3.846a.75.75 0 01-1.02 0L5.25 8.27a.75.75 0 01-.02-1.06z"
                                        fill="#374151"
                                      />
                                    </svg>
                                  </AccordionCaretIcon>
                                </AccordionCaretButton>
                              </TableCell>
                            </TableRow>
                            {isAnalyticsAccordionOpen && (
                              <AnalyticsAccordionContentRow>
                                <AnalyticsAccordionCell colSpan={3} id={accordionId}>
                                  {ANALYTICS_CARD_PERMISSIONS.map((card) => {
                                    const checked = analyticsPermissions?.[card.key];
                                    const isLockedByAdmin = isAdminOption === true;
                                    const requiresLogin = !hasLogin && !isAdminOption;
                                    // Feedback card requires readFeedback toggle to be on
                                    const requiresReadFeedback = card.key === 'feedback' && !readFeedback && !isAdminOption;
                                    const analyticsDisabled = isLockedByAdmin || requiresLogin || requiresReadFeedback;
                                    let analyticsDisabledReason = '';
                                    if (isLockedByAdmin) {
                                      analyticsDisabledReason = 'Locked by Administrator';
                                    } else if (requiresLogin) {
                                      analyticsDisabledReason = 'Requires Login Access';
                                    } else if (requiresReadFeedback) {
                                      analyticsDisabledReason = 'Enable "Verbatim Comment Access" to use this feature';
                                    }

                                    return (
                                      <AnalyticsPermissionRow key={card.key}>
                                        <AnalyticsPermissionInfo>
                                          <AnalyticsPermissionTitle>
                                            {card.label}
                                          </AnalyticsPermissionTitle>
                                          <AnalyticsPermissionDescription>
                                            {card.description}
                                          </AnalyticsPermissionDescription>
                                        </AnalyticsPermissionInfo>
                                        <ToggleWithLock>
                                          {analyticsDisabled && (
                                            <LockIcon
                                              aria-label={analyticsDisabledReason || 'Locked'}
                                              title={analyticsDisabledReason || 'Locked'}
                                            >
                                              <LockSvg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                                <path
                                                  d="M6 8V6a4 4 0 118 0v2h1a1 1 0 011 1v8a1 1 0 01-1 1H5a1 1 0 01-1-1V9a1 1 0 011-1h1zm2-2a2 2 0 114 0v2H8V6z"
                                                  fill="#9ca3af"
                                                />
                                              </LockSvg>
                                            </LockIcon>
                                          )}
                                          {analyticsDisabled && requiresReadFeedback ? (
                                            <TooltipWrapperWithPortal tooltipText={analyticsDisabledReason}>
                                              <ToggleComponent
                                                checked={Boolean(checked)}
                                                handleCheck={(value) =>
                                                  handleAnalyticsPermissionChange(card.key, value)
                                                }
                                                disabled={analyticsDisabled}
                                              />
                                            </TooltipWrapperWithPortal>
                                          ) : (
                                              <ToggleComponent
                                                checked={Boolean(checked)}
                                                handleCheck={(value) =>
                                                  handleAnalyticsPermissionChange(card.key, value)
                                                }
                                                disabled={analyticsDisabled}
                                              />
                                          )}
                                        </ToggleWithLock>
                                      </AnalyticsPermissionRow>
                                    );
                                  })}
                                </AnalyticsAccordionCell>
                              </AnalyticsAccordionContentRow>
                            )}
                          </React.Fragment>
                        );
                      }

                      let checked = false;
                      let disabled = false;
                      let disabledReason = "";
                      let onToggle = (value) => {};

                      switch (permission.key) {
                        case "hasLogin":
                          checked = hasLogin;
                          disabled = isAdminOption === true;
                          if (disabled) disabledReason = "Locked by Administrator";
                          onToggle = (value) => setHasLogin(value);
                          break;
                        case "pdfReports":
                          checked = pdfReports;
                          disabled = isAdminOption === true;
                          if (disabled) disabledReason = "Locked by Administrator";
                          onToggle = (value) => setPdfReports(value);
                          break;
                        case "noraAI":
                          checked = noraAI;
                          disabled = isAdminOption === true;
                          if (disabled) disabledReason = "Locked by Administrator";
                          onToggle = (value) => setNoraAI(value);
                          break;
                        case "specialPermission":
                          checked = specialPermission;
                          disabled = isAdminOption === true;
                          if (disabled) disabledReason = "Locked by Administrator";
                          onToggle = (value) => setSpecialPermission(value);
                          break;
                        case "readFeedback":
                          checked = readFeedback;
                          disabled = isAdminOption === true;
                          if (disabled) disabledReason = "Locked by Administrator";
                          onToggle = (value) => {
                            setReadFeedback(value);
                            // If turning off readFeedback, also turn off feedback analytics card
                            if (!value) {
                              setAnalyticsPermissions((prev) => ({
                                ...prev,
                                feedback: false,
                              }));
                            }
                          };
                          break;
                        case "scheduleSurveys":
                          checked = scheduleSurveys;
                          disabled = isAdminOption === true;
                          if (disabled) disabledReason = "Locked by Administrator";
                          onToggle = (value) => {
                            setScheduleSurveys(value);
                            if (value === true) {
                              // Requires Manage Contacts
                              setEditUsers(true);
                            }
                          };
                          break;
                        case "editUsers":
                          checked = editUsers || scheduleSurveys; // lock ON if scheduleSurveys is ON
                          disabled = isAdminOption === true || scheduleSurveys === true;
                          if (isAdminOption) {
                            disabledReason = "Locked by Administrator";
                          } else if (scheduleSurveys) {
                            disabledReason = "Required by Schedule Surveys";
                          }
                          onToggle = (value) => setEditUsers(value);
                          break;
                        default:
                          return null;
                      }

                      return (
                        <TableRow key={permission.key}>
                          <TableCell>
                            <RowLabelContainer>
                              <PermissionLabel>{permission.label}</PermissionLabel>
                            </RowLabelContainer>
                          </TableCell>
                          <TableCell>
                            <PermissionDescription>
                              {permission.description}
                            </PermissionDescription>
                          </TableCell>
                          <TableCell style={{ textAlign: 'center' }}>
                            <ToggleWithLock>
                              {disabled && (
                                <LockIcon aria-label={disabledReason || 'Locked'} title={disabledReason || 'Locked'}>
                                  <LockSvg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M6 8V6a4 4 0 118 0v2h1a1 1 0 011 1v8a1 1 0 01-1 1H5a1 1 0 01-1-1V9a1 1 0 011-1h1zm2-2a2 2 0 114 0v2H8V6z" fill="#9ca3af" />
                                  </LockSvg>
                                </LockIcon>
                              )}
                              <ToggleComponent
                                checked={checked}
                                handleCheck={onToggle}
                                disabled={disabled}
                              />
                            </ToggleWithLock>
                          </TableCell>
                        </TableRow>
                      );
                    })}
              </React.Fragment>
            ))}
          </TableBody>
        </PermissionsTable>
      </PermissionsContent>

      {/* Account validation appears only when Login Access is enabled */}
      {hasLogin && (
        <AccountCard>
          <AccountHeader>
            <AccountTitle>Account</AccountTitle>
          </AccountHeader>
          <AccountBody>
            <AccountRow>
              <AccountLabel>Validation</AccountLabel>
              <AccountContent>
                {(() => {
                  const source = employeeById || manager;
                  const isValidated = source?.email_status_text === "Validated";
                  return (
                    <ValidatedSection>
                      <ValidText valid={isValidated}>
                        {isValidated ? "Validated" : "Pending"}
                      </ValidText>
                      {!isValidated ? (
                        <SignupToken
                          organization_id={manager?.organization || get_auth?.organization_id}
                          employee_id={manager?.id}
                        />
                      ) : (
                        <EmailDeliveryReport employee_id={manager?.id} />
                      )}
                    </ValidatedSection>
                  );
                })()}
              </AccountContent>
            </AccountRow>
          </AccountBody>
        </AccountCard>
      )}
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  padding: 24px;
  background: white;
  min-height: 400px;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 12px;
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e5e7eb;
`;

const SaveButton = styled.button`
  background: #3b82f6;
  border: none;
  border-radius: 8px;
  color: white;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  padding: 12px 24px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-left: auto;

  &:hover:not(:disabled) {
    background: #2563eb;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const WarningBanner = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  max-width: 70%;
  background: #FEF3C7;
  color: #92400E;
  border: 1px solid #F59E0B;
  border-radius: 8px;
  padding: 10px 12px;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 12px;
`;

const AssignLinkButton = styled.button`
  background: transparent;
  border: none;
  color: #92400E;
  text-decoration: underline;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
  padding: 0;
  &:hover { opacity: 0.85; }
`;

// (Details toggle removed per requirements)

const PermissionsContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const PermissionsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const TableHeader = styled.thead`
  background: #f8f9fa;
`;

const HeaderRow = styled.tr``;

const HeaderCell = styled.th`
  padding: 16px;
  text-align: left;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  &:hover {
    background: #f9fafb;
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid #f3f4f6;
  }
`;

const TableCell = styled.td`
  padding: 16px;
  vertical-align: top;
`;

const PermissionLabel = styled.span`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
`;

const PermissionDescription = styled.span`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.4;
`;

const AccordionCaretButton = styled.button`
  background: transparent;
  border: none;
  padding: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover svg path {
    fill: #111827;
  }

  &:focus {
    outline: none;
  }
`;

const AccordionCaretIcon = styled.span`
  display: inline-flex;
  width: 20px;
  height: 20px;
  transition: transform 0.2s ease;
  transform: ${(props) => (props.$open ? 'rotate(180deg)' : 'rotate(0deg)')};

  svg {
    width: 20px;
    height: 20px;
  }
`;

const AnalyticsAccordionContentRow = styled.tr`
  background: #fbfbfc;
`;

const AnalyticsAccordionCell = styled.td`
  padding: 0 16px 16px 16px;
`;

const AnalyticsPermissionRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }
`;

const AnalyticsPermissionInfo = styled.div`
  max-width: 70%;
`;

const AnalyticsPermissionTitle = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 13px;
  color: #1f2937;
`;

const AnalyticsPermissionDescription = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
`;

// Grouping styles
const GroupHeaderRow = styled.tr`
  background: #f3f4f6;
`;

const GroupHeaderCell = styled.td`
  padding: 14px 16px;
  border-top: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
`;

const GroupTitle = styled.div`
  font-family: "Poppins";
  font-weight: 700;
  font-size: 13px;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

// Inline label and tooltip helpers
const RowLabelContainer = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const ToggleWithLock = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const TooltipWrapperPortal = styled.span`
  position: relative;
  display: inline-block;
`;

const TooltipTextPortal = styled.div`
  position: fixed;
  transform: translate(-50%, -100%);
  margin-bottom: 8px;
  background-color: #1f2937;
  color: #ffffff;
  text-align: center;
  padding: 8px 12px;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 12px;
  font-weight: 400;
  white-space: nowrap;
  z-index: 99999;
  pointer-events: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  opacity: 1;
  visibility: visible;

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: #1f2937;
  }
`;

// Simple wrapper for radio inputs in the table
const RadioWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

// Custom radio styled to match toggle energy
const RadioInput = styled.input.attrs({ type: 'radio' })`
  appearance: none;
  -webkit-appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #cbd5e1; /* slate-300 */
  background: #ffffff;
  position: relative;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: #94a3b8; /* slate-400 */
  }

  &:checked {
    border-color: #12B981;
  }

  &:checked::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 10px;
    height: 10px;
    background: #12B981;
    border-radius: 50%;
    transform: translate(-50%, -50%);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(18, 185, 129, 0.25);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// (Info tooltip/icon removed per requirements)

const LockIcon = styled.div`
  width: 18px;
  height: 18px;
`;

const LockSvg = styled.svg`
  width: 18px;
  height: 18px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
`;

const EmptyTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 18px;
  color: #414141;
  margin: 0 0 8px 0;
`;

const EmptyText = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 14px;
  color: #7e7e7e;
  margin: 0;
`;

// Toggle Component Styles (from EditAudit.js)
const ToggleLabel = styled.label`
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
`;

const ToggleSlider = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${props => props.isChecked ? '#12B981' : '#cbd5e1'};
  transition: 0.2s;
  border-radius: 24px;

  &:before {
    position: absolute;
    content: "";
    height: 20px;
    width: 20px;
    left: ${props => props.isChecked ? '22px' : '2px'};
    bottom: 2px;
    background-color: white;
    transition: 0.2s;
    border-radius: 50%;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
`;

// Account validation styles
const AccountCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  margin-bottom: 24px;
  overflow: hidden;
`;

const AccountHeader = styled.div`
  padding: 14px 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #e5e7eb;
`;

const AccountTitle = styled.div`
  font-family: "Poppins";
  font-weight: 700;
  font-size: 13px;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const AccountBody = styled.div`
  padding: 12px 16px;
`;

const AccountRow = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
`;

const AccountLabel = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
`;

const AccountContent = styled.div`
  display: flex;
  align-items: center;
`;

const ValidatedSection = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
`;

const ValidText = styled.span`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 12px;
  padding: 6px 10px;
  border-radius: 9999px;
  color: ${(props) => (props.valid ? "#065f46" : "#92400e")};
  background: ${(props) => (props.valid ? "#d1fae5" : "#fef3c7")};
  border: 1px solid ${(props) => (props.valid ? "#10b981" : "#f59e0b")};
`;

export default ManagerPermissions;
